import React, { useContext, useEffect, useState } from 'react';
import { Button, DataType, EllipsisSpan, Paging, Table, usePaging } from '@maxtropy/components';
import styles from './index.module.scss';
import { EnergyUsageCostContext } from '@/pages/EnergyUsageCostKanban';
import { isNil } from 'lodash-es';
import { SorterResult } from 'antd/es/table/interface';
import { TableProps } from 'antd';
import {
  apiV2EnergyUnitPrimaryEnergyUsingUnitPagePost,
  V2EnergyUnitPrimaryEnergyUsingUnitPagePostResponse,
  apiV2EnergyUnitPrimaryEnergyUsingUnitPageHeadListPost,
  V2EnergyUnitPrimaryEnergyUsingUnitPageHeadListPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { EnergyAnalysisOverviewContext } from '../..';

interface SubUnitTableProps {
  swiperUnit?: {
    unitId?: number;
    unitName?: string;
  };
}

type HeaderItemProps = Exclude<V2EnergyUnitPrimaryEnergyUsingUnitPageHeadListPostResponse['list'], undefined>[number];
type EnergyUsingUnitItemProps = Exclude<V2EnergyUnitPrimaryEnergyUsingUnitPagePostResponse['list'], undefined>[number];

const SubUnitTable: React.FC<SubUnitTableProps> = ({ swiperUnit }) => {
  const { groupId, setUnitId, setShowDetail, oneLevelUnits } = useContext(EnergyUsageCostContext);
  const { time, dataQueryPeriodId, timeResolution } = useContext(EnergyAnalysisOverviewContext);
  const [dataSource, setDataSource] = useState<any>([]);
  const [dataColumns, setDataColumns] = useState<any>([]);
  const [sortedInfo, setSortedInfo] = useState<SorterResult<DataType>>({});
  // const [generalData, setGeneralData] = useState<GeneralListProps>();
  const [headerData, setHeaderData] = useState<HeaderItemProps[]>();
  const [tableOriginalData, setTableOriginalData] = useState<EnergyUsingUnitItemProps[]>();
  const [loading, setLoading] = useState<boolean>(false);

  const pagingInfo = usePaging();
  const { pageOffset, pageSize, setTotalCount } = pagingInfo;

  let columns: any[] = [
    {
      title: '用能单元',
      dataIndex: 'name',
      width: 150,
      key: 'name',
      fixed: 'left' as const,
    },
    {
      title: '层级',
      width: 120,
      dataIndex: 'level',
      key: 'level',
    },
    {
      title: '总成本(元)',
      width: 120,
      dataIndex: 'totalCost',
      key: 'totalCost',
    },
    {
      title: '操作',
      dataIndex: 'operation',
      width: 60,
      fixed: 'right' as const,
      render: (value: undefined, record: any) => {
        return (
          <Button
            type="link"
            onClick={() => {
              setShowDetail?.(true);
              setUnitId?.(record.key);
            }}
          >
            详情
          </Button>
        );
      },
    },
  ];

  useEffect(() => {
    if (isNil(headerData) || isNil(tableOriginalData)) return;
    const headers = headerData
      .filter((header, idx) => !(header.name?.includes('总成本') && idx === 0))
      ?.map((m, index) => {
        return {
          title: m.name,
          dataIndex: m.id,
          key: m.id,
          width: 170,
          // sortOrder: sortedInfo.columnKey === m.id ? sortedInfo.order : null,
          // sorter: (a: number[], b: number[]) => a[m.id] - b[m.id!],
          render: (v: number) => (!isNil(v) ? <EllipsisSpan value={v} /> : '--'),
        };
      });
    if (!isNil(tableOriginalData?.[0]?.periodTotalCostDetail)) {
      headers.unshift({
        title: tableOriginalData[0].periodTotalCostDetail?.name,
        dataIndex: 'periodTotalCost',
        key: 'periodTotalCost',
        width: 170,
        // sortOrder: sortedInfo.columnKey === m.id ? sortedInfo.order : null,
        // sorter: (a: number[], b: number[]) => a[m.id] - b[m.id!],
        render: (v: number) => (!isNil(v) ? <EllipsisSpan value={v} /> : '--'),
      });
    }

    columns.splice(3, 0, ...headers);
    setDataColumns([...columns]);
    const data = tableOriginalData?.map(item => {
      const columnData = {} as any;
      headers.forEach((column, index) => {
        columnData[column.key!] = item.costDetailList?.find(detail => detail.id === column.key)?.mediumValue;
      });

      return {
        ...columnData,
        key: item.energyUnitId,
        name: item.energyUnitName,
        level: item.level,
        totalCost: item.totalCost ?? '--',
        periodTotalCost: item.periodTotalCostDetail?.periodTotalCost,
      };
    });
    setDataSource([...data]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerData, tableOriginalData]);

  useEffect(() => {
    const { unitId } = swiperUnit || {};
    if (isNil(groupId) || isNil(unitId) || !time! || !time![0] || !time![1] || oneLevelUnits!.length === 0) {
      setDataColumns([...columns]);
      setDataSource([]);
      return;
    }
    if (!oneLevelUnits!.some(item => item.energyUnitGroupId === groupId && item.energyUnitId === unitId)) return;
    const params = {
      fromTime: time![0]!.valueOf(),
      toTime: time![1]!.valueOf(),
      timeResolution,
      periodId: dataQueryPeriodId,
      energyUnitGroupId: String(groupId),
      energyUnitId: String(unitId),
    };
    // 表头
    apiV2EnergyUnitPrimaryEnergyUsingUnitPageHeadListPost({
      ...params,
      page: 1,
      size: 100,
    }).then(res => {
      setSortedInfo({});
      setHeaderData(res.list ?? []);
    });

    // 表格数据
    setLoading(true);
    apiV2EnergyUnitPrimaryEnergyUsingUnitPagePost({
      ...params,
      page: pageOffset,
      size: pageSize,
    })
      .then(res => {
        setSortedInfo({});
        setTotalCount(res?.total ?? 0);
        setTableOriginalData(res.list ?? []);
      })
      .finally(() => {
        setLoading(false);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swiperUnit, time, dataQueryPeriodId, pageOffset, pageSize]);

  const handleChange: TableProps<DataType>['onChange'] = (pagination, filters, sorter) => {
    setSortedInfo(sorter as SorterResult<DataType>);
  };

  return (
    <div className={styles.tableBox}>
      <div className={styles.title}>{swiperUnit?.unitName}下级单元用能成本</div>
      <Table
        loading={loading}
        dataSource={dataSource}
        columns={dataColumns}
        pagination={false}
        scroll={{ x: 1000, y: 'calc(100vh - 480px)' }}
        onChange={handleChange}
      />
      <Paging pagingInfo={pagingInfo} />
    </div>
  );
};

export default SubUnitTable;
